import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, fetchApi } from './api';
import { useAuth } from './auth';
import {
  appIdentityScope,
  captureMutationAdmission,
  scopeQueryKey,
  useSyncIdentityScope,
} from './identity-scope';
import {
  CONTROL_ONBOARDING_ENDPOINT,
  CONTROL_STATUS_ENDPOINT,
  PREFERENCES_ENDPOINT,
} from './api-contract';
import type { UpdatePreferencesPayload, UserPreferences } from '@/types';

/* ──────────────────────────────────────────────────────────────────────── */
/* APP-W01c seam — identity-scoped private query keys                        */
/* ──────────────────────────────────────────────────────────────────────── */

/**
 * Sentinel marker prefix for disabled (identity-less) private keys. Keys
 * carrying this tag are never fetched (the enabled gate is false alongside).
 */
const QUERY_SCOPE_TAG = 'query-scope-1';

/**
 * APP-W01c: scope a logical private key by the CURRENT confirmed identity
 * (shared appIdentityScope synchronised by useSyncIdentityScope). Returns
 * null with no confirmed identity: private queries are DISABLED before
 * authenticated identity (fail closed).
 */
export function scopedQueryKey(logical: readonly unknown[]): readonly unknown[] | null {
  return scopeQueryKey(appIdentityScope, logical);
}

function useAppQueryContext() {
  const { isAuthenticated, user, org } = useAuth();
  const enabled = isAuthenticated && !!user?.userId && !!org?.orgId;
  useSyncIdentityScope(enabled && user ? user : null, enabled && org ? org : null);
  return {
    scopedKey: (logical: readonly unknown[]): readonly unknown[] | null =>
      enabled ? scopedQueryKey(logical) : null,
    enabled,
  };
}

/** Session-required classification for a mutation admitted without identity. */
function sessionRequiredError(): ApiError {
  return new ApiError('Sign-in required. Please authenticate.', 401, false, 'SESSION_REQUIRED');
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Private query hooks (identity-scoped)                                     */
/* ──────────────────────────────────────────────────────────────────────── */

export const queryKeys = {
  controlStatus: ['control', 'status'] as const,
  controlOnboarding: ['control', 'onboarding'] as const,
};

export const preferencesQueryKey = ['preferences'] as const;

export function usePreferences() {
  const { scopedKey, enabled } = useAppQueryContext();
  const key = scopedKey(preferencesQueryKey);
  return useQuery<UserPreferences, ApiError>({
    queryKey: key ?? [QUERY_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<UserPreferences>(PREFERENCES_ENDPOINT, { signal }),
    enabled: enabled && !!key,
  });
}

export function useUpdatePreferences() {
  const { enabled } = useAppQueryContext();
  const queryClient = useQueryClient();

  return useMutation<UserPreferences, ApiError, UpdatePreferencesPayload>({
    mutationFn: (payload) => {
      // Mutation admission (W01c-C1/C2): capture the identity BEFORE the
      // request leaves. The ADMISSION snapshot — not a settled-time
      // identity lookup — owns this call's cache key.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<UserPreferences>(PREFERENCES_ENDPOINT, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }).then((data) => {
        // Settle-time fence, THIS call's admission: a stale A response
        // performs ZERO cache mutation under B (the departed subtree and
        // the current B keys are both refused).
        if (admission.isCurrent()) {
          queryClient.setQueryData(admission.key(preferencesQueryKey), data);
        }
        return data;
      });
    },
  });
}

type ControlStatusResponse = {
  orgId: string;
  secrets: Record<string, { hasValue: boolean }>;
  control: {
    outboxOk: boolean;
    auditOk: boolean;
    webhooksConfigured: boolean;
  };
};

/**
 * Mirrors GET/PATCH /control/onboarding (backend routes/control.ts): per-step
 * booleans only — the backend returns no completion timestamp, so "complete"
 * is derived client-side from the step flags.
 */
export type OnboardingState = {
  orgId: string;
  clinic: {
    profileDone: boolean;
    servicesDone: boolean;
    availabilityDone: boolean;
  };
  staffing: {
    icpDone: boolean;
    firstRunDone: boolean;
  };
};

export function useControlStatus() {
  const { scopedKey, enabled } = useAppQueryContext();
  const key = scopedKey(queryKeys.controlStatus);
  return useQuery<ControlStatusResponse, ApiError>({
    queryKey: key ?? [QUERY_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<ControlStatusResponse>(CONTROL_STATUS_ENDPOINT, { signal }),
    staleTime: 30000,
    enabled: enabled && !!key,
  });
}

export function useOnboardingState() {
  const { scopedKey, enabled } = useAppQueryContext();
  const key = scopedKey(queryKeys.controlOnboarding);
  return useQuery<OnboardingState, ApiError>({
    queryKey: key ?? [QUERY_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<OnboardingState>(CONTROL_ONBOARDING_ENDPOINT, { signal }),
    staleTime: 10000,
    enabled: enabled && !!key,
  });
}

export function useUpdateOnboardingState() {
  const { enabled } = useAppQueryContext();
  const queryClient = useQueryClient();

  return useMutation<
    OnboardingState,
    ApiError,
    Partial<{
      clinic: Partial<OnboardingState['clinic']>;
      staffing: Partial<OnboardingState['staffing']>;
    }>
  >({
    mutationFn: (updates) => {
      // Mutation admission (W01c-C1/C2): capture BEFORE the wire call; the
      // captured admission, not a settled-time identity lookup, owns the
      // settle-time cache write.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<OnboardingState>(CONTROL_ONBOARDING_ENDPOINT, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }).then((data) => {
        // Settle-time fence on THIS call's admission: a stale A response
        // never writes under B.
        if (admission.isCurrent()) {
          queryClient.setQueryData(admission.key(queryKeys.controlOnboarding), data);
        }
        return data;
      });
    },
  });
}

export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

export function isRetryableError(error: unknown): boolean {
  return error instanceof ApiError && error.retryable;
}

/** Friendly copy for backend error codes, used when the response carried no message. */
const CODE_MESSAGES: Record<string, string> = {
  AI_KEY_MISSING: 'Run failed - check your API keys in Settings',
  INGEST_KEY_MISSING: 'Data provider key not configured - check Settings',
  INGEST_FAILED: 'Data provider request failed',
  AI_PARSE_FAILED: 'AI response could not be processed',
  RUN_FAILED: 'Run failed - please try again or contact support',
  EXPORT_NOT_READY: 'Run has no export artifacts yet',
  INTERNAL: 'Something went wrong',
};

export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // The backend error string is already sanitized — prefer it verbatim.
    if (error.message) return error.message;
    if (error.code && CODE_MESSAGES[error.code]) return CODE_MESSAGES[error.code]!;
    return 'Something went wrong';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
